import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle, PieChart } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'budget_calculator_data';

const BudgetCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyIncome : 5000;
  });
  
  const [fixedExpenses, setFixedExpenses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).fixedExpenses : 2000;
  });
  
  const [variableExpenses, setVariableExpenses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).variableExpenses : 1500;
  });
  
  const [savingsGoal, setSavingsGoal] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).savingsGoal : 1000;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      monthlyIncome,
      fixedExpenses,
      variableExpenses,
      savingsGoal
    }));
  }, [monthlyIncome, fixedExpenses, variableExpenses, savingsGoal]);

  const calculateBudget = () => {
    const income = parseFloat(monthlyIncome) || 0;
    const fixed = parseFloat(fixedExpenses) || 0;
    const variable = parseFloat(variableExpenses) || 0;
    const goal = parseFloat(savingsGoal) || 0;

    const totalExpenses = fixed + variable;
    const remainingBalance = income - totalExpenses;
    const actualSavings = Math.max(0, remainingBalance);
    const savingsRate = income > 0 ? (actualSavings / income) * 100 : 0;
    const expenseRate = income > 0 ? (totalExpenses / income) * 100 : 0;
    
    // 50/30/20 Rule Analysis
    const needs = fixed; // Assume fixed expenses are needs
    const wants = variable; // Assume variable expenses are wants
    const needsPercent = income > 0 ? (needs / income) * 100 : 0;
    const wantsPercent = income > 0 ? (wants / income) * 100 : 0;
    const savingsPercent = savingsRate;
    
    // Ideal 50/30/20
    const idealNeeds = income * 0.50;
    const idealWants = income * 0.30;
    const idealSavings = income * 0.20;
    
    const status = remainingBalance >= goal ? 'On Track' : 
                   remainingBalance > 0 ? 'Underfunded' : 'Overspending';
    
    return {
      totalExpenses,
      remainingBalance,
      actualSavings,
      savingsRate,
      expenseRate,
      status,
      needsPercent,
      wantsPercent,
      savingsPercent,
      idealNeeds,
      idealWants,
      idealSavings,
      needsGap: needs - idealNeeds,
      wantsGap: wants - idealWants,
      savingsGap: actualSavings - idealSavings
    };
  };

  const generateExpenseBreakdown = () => {
    const income = parseFloat(monthlyIncome) || 0;
    const fixed = parseFloat(fixedExpenses) || 0;
    const variable = parseFloat(variableExpenses) || 0;
    const remaining = Math.max(0, income - fixed - variable);
    
    return [
      { name: 'Fixed Expenses', value: fixed, color: 'hsl(var(--chart-1))' },
      { name: 'Variable Expenses', value: variable, color: 'hsl(var(--chart-2))' },
      { name: 'Remaining/Savings', value: remaining, color: 'hsl(var(--chart-3))' }
    ].filter(item => item.value > 0);
  };

  const generate502020Comparison = () => {
    const results = calculateBudget();
    return [
      {
        category: 'Needs (50%)',
        actual: results.needsPercent,
        ideal: 50,
        actualAmount: parseFloat(fixedExpenses) || 0,
        idealAmount: results.idealNeeds
      },
      {
        category: 'Wants (30%)',
        actual: results.wantsPercent,
        ideal: 30,
        actualAmount: parseFloat(variableExpenses) || 0,
        idealAmount: results.idealWants
      },
      {
        category: 'Savings (20%)',
        actual: results.savingsPercent,
        ideal: 20,
        actualAmount: results.actualSavings,
        idealAmount: results.idealSavings
      }
    ];
  };

  const generateMonthlyTrend = () => {
    const income = parseFloat(monthlyIncome) || 0;
    const expenses = parseFloat(fixedExpenses) + parseFloat(variableExpenses) || 0;
    const savings = Math.max(0, income - expenses);
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map((month) => ({
      month,
      income: Math.round(income),
      expenses: Math.round(expenses),
      savings: Math.round(savings)
    }));
  };

  const results = calculateBudget();
  const expenseData = generateExpenseBreakdown();
  const comparison502020 = generate502020Comparison();
  const trendData = generateMonthlyTrend();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary-light/20 to-background">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Budget Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Tips
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Budget Planning?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Budget planning helps you track income and expenses to ensure you're living within your means and 
                  saving for the future. The 50/30/20 rule suggests allocating 50% to needs, 30% to wants, and 20% to savings.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Your Finances</CardTitle>
                  <CardDescription>Enter your monthly income and expenses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyIncome" className="text-sm font-medium">
                      Monthly Income ($)
                    </Label>
                    <Input
                      id="monthlyIncome"
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      placeholder="5000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fixedExpenses" className="text-sm font-medium">
                      Fixed Expenses ($)
                    </Label>
                    <Input
                      id="fixedExpenses"
                      type="number"
                      value={fixedExpenses}
                      onChange={(e) => setFixedExpenses(e.target.value)}
                      placeholder="2000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Rent, utilities, insurance, EMI</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="variableExpenses" className="text-sm font-medium">
                      Variable Expenses ($)
                    </Label>
                    <Input
                      id="variableExpenses"
                      type="number"
                      value={variableExpenses}
                      onChange={(e) => setVariableExpenses(e.target.value)}
                      placeholder="1500"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Food, shopping, entertainment, travel</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="savingsGoal" className="text-sm font-medium">
                      Savings Goal ($)
                    </Label>
                    <Input
                      id="savingsGoal"
                      type="number"
                      value={savingsGoal}
                      onChange={(e) => setSavingsGoal(e.target.value)}
                      placeholder="1000"
                      className="text-base"
                    />
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setMonthlyIncome(monthlyIncome)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate Budget
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-display">Budget Summary</CardTitle>
                        <CardDescription>Your financial overview</CardDescription>
                      </div>
                      <Badge 
                        className={`px-4 py-2 text-sm ${
                          results.status === 'On Track' ? 'bg-secondary/20 text-secondary' :
                          results.status === 'Underfunded' ? 'bg-warning/20 text-warning' :
                          'bg-destructive/20 text-destructive'
                        }`}
                      >
                        {results.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(parseFloat(monthlyIncome))}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.totalExpenses)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{results.expenseRate.toFixed(1)}% of income</p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${
                        results.remainingBalance >= 0 ? 'from-secondary-light to-card border-secondary/20' : 'from-warning-light to-card border-warning/20'
                      } border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${
                            results.remainingBalance >= 0 ? 'bg-secondary' : 'bg-warning'
                          } flex items-center justify-center`}>
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Remaining Balance</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${
                          results.remainingBalance >= 0 ? 'text-secondary' : 'text-warning'
                        }`}>
                          {formatCurrency(results.remainingBalance)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <PieChart className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Savings Rate</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {results.savingsRate.toFixed(1)}%
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{formatCurrency(results.actualSavings)} per month</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Income vs Expenses</CardTitle>
                <CardDescription className="text-base">
                  Track your monthly financial flow
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="month" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="income" fill="hsl(var(--chart-1))" name="Income" />
                      <Bar dataKey="expenses" fill="hsl(var(--chart-5))" name="Expenses" />
                      <Bar dataKey="savings" fill="hsl(var(--chart-2))" name="Savings" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Expense Breakdown</CardTitle>
                  <CardDescription>
                    How your money is distributed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={expenseData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {expenseData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">50/30/20 Rule Analysis</CardTitle>
                  <CardDescription>
                    Actual vs ideal allocation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={comparison502020}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="category" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="actual" fill="hsl(var(--chart-1))" name="Your Allocation" />
                        <Bar dataKey="ideal" fill="hsl(var(--chart-2))" name="Ideal (50/30/20)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Budget Analysis & Recommendations</CardTitle>
                <CardDescription className="text-base">
                  Understand your spending patterns and get personalized advice
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Understanding the 50/30/20 Rule
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    The 50/30/20 rule is a simple budgeting framework that suggests dividing your after-tax income into three categories:
                  </p>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ 50% for Needs</h4>
                      <p className="text-muted-foreground">
                        Essential expenses like rent, utilities, insurance, groceries, and minimum debt payments.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ 30% for Wants</h4>
                      <p className="text-muted-foreground">
                        Non-essential spending like dining out, entertainment, hobbies, and subscriptions.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ 20% for Savings</h4>
                      <p className="text-muted-foreground">
                        Emergency fund, retirement contributions, investments, and extra debt payments.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Your Current Allocation
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-gradient-chart border border-border">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Needs</p>
                      <p className="text-2xl font-bold text-foreground mb-2">
                        {results.needsPercent.toFixed(1)}%
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {results.needsGap < 0 ? `${formatCurrency(Math.abs(results.needsGap))} under` : `${formatCurrency(results.needsGap)} over`}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-gradient-chart border border-border">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Wants</p>
                      <p className="text-2xl font-bold text-foreground mb-2">
                        {results.wantsPercent.toFixed(1)}%
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {results.wantsGap < 0 ? `${formatCurrency(Math.abs(results.wantsGap))} under` : `${formatCurrency(results.wantsGap)} over`}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-gradient-chart border border-border">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Savings</p>
                      <p className="text-2xl font-bold text-foreground mb-2">
                        {results.savingsPercent.toFixed(1)}%
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {results.savingsGap < 0 ? `${formatCurrency(Math.abs(results.savingsGap))} short` : `${formatCurrency(results.savingsGap)} above`}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-warning">
                    Budget Optimization Tips
                  </h3>
                  <div className="space-y-3 text-base">
                    {results.savingsRate < 15 && (
                      <div className="flex gap-3">
                        <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                        <div>
                          <strong className="text-foreground">Low Savings Rate:</strong> Try to increase your savings to at least 20% of income. 
                          Look for areas to cut back in your variable expenses.
                        </div>
                      </div>
                    )}
                    {results.needsPercent > 55 && (
                      <div className="flex gap-3">
                        <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                        <div>
                          <strong className="text-foreground">High Fixed Costs:</strong> Your needs exceed the recommended 50%. 
                          Consider ways to reduce fixed expenses like negotiating rent or refinancing loans.
                        </div>
                      </div>
                    )}
                    {results.wantsPercent > 35 && (
                      <div className="flex gap-3">
                        <span className="text-warning font-semibold flex-shrink-0">⚠️</span>
                        <div>
                          <strong className="text-foreground">Discretionary Spending:</strong> Your wants spending is above 30%. 
                          Review subscriptions, dining out, and entertainment expenses for savings opportunities.
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Track Everything:</strong> Use a budgeting app or spreadsheet to track every expense for a month. 
                        You'll discover spending patterns you didn't know existed.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Automate Savings:</strong> Set up automatic transfers to savings accounts on payday. 
                        Treat savings like a non-negotiable bill.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-secondary font-semibold flex-shrink-0">✅</span>
                      <div>
                        <strong className="text-foreground">Review Regularly:</strong> Revisit your budget monthly to adjust for changes in income or expenses. 
                        Life changes, and so should your budget.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BudgetCalculator;
